import { captureException } from '@sentry/react';
import { useEffect } from 'react';
import { isCanceledApiMessage, handleCallCenterOnlineTimeTracking } from '@/shared/api';
import { getTabId } from '@/shared/lib';
import { detectBrowser } from '@/shared/lib/browser';

// Hardcoded app identity sent with every online signal so the backend can attribute
// the session to this portal.
const TAB_NAME = 'AIRP';

// Presence online-signal cadence: BE aggregates signals into online-time sessions.
const ONLINE_SIGNAL_INTERVAL = 60 * 1000;

// Invisible, app-wide presence online signal. Fires one signal on mount and then on a
// fixed ~60s cadence for as long as the authenticated shell is mounted (until the
// tab/browser closes). Sends `tabId`, `tabName`, and `browser`; the backend derives
// the user/portal from the request headers and owns all online-time computation. No stop call,
// no session state, no role-gating: the backend decides eligibility per signal.
/**
 * Keeps the call-center online-time heartbeat running for as long as the app is mounted. Fires one signal immediately on mount using the synchronously-available tab key and then repeats on the online-signal interval, clearing the interval on unmount.
 *
 * @returns Null, since the component renders nothing.
 */
const OnlineTimeTracking = (): null => {
  useEffect(() => {
    /**
     * Reports this tab as online for call-center time tracking. Sends the tab key with the tab name and detected browser, swallowing deliberate cancellations while reporting other failures to Sentry and the console.
     *
     * @returns Nothing.
     */
    const sendOnlineSignal = () => {
      handleCallCenterOnlineTimeTracking({
        tabId: getTabId(),
        tabName: TAB_NAME,
        browser: detectBrowser(navigator.userAgent),
      }).catch((error: any) => {
        if (isCanceledApiMessage(error?.message)) {
          return;
        }
        captureException(error);
        console.error('Failed to send online-time signal: ', error);
      });
    };

    // The tab key is available synchronously at mount, so the first beat fires straight away
    // and the cadence needs nothing to poll or wait for.
    sendOnlineSignal();
    const onlineSignalIntervalId = setInterval(sendOnlineSignal, ONLINE_SIGNAL_INTERVAL);

    return () => {
      clearInterval(onlineSignalIntervalId);
    };
  }, []);

  return null;
};

export default OnlineTimeTracking;
